import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  InputLabel,
  Link as MuiLink,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AdminCategory,
  AdminItem,
  ApiError,
  ItemCreate,
  ItemPatch,
  Personality,
  createItem,
  generateDescription,
  getItem,
  listPersonalities,
  patchItem,
  scrapeDescription,
  setItemImage,
  uploadItemImage,
} from '../../api';
import { useAuth } from '../../auth/AuthContext';
import ItemImage from '../widgets/ItemImage';

// ---------------------------------------------------------------------------
// Form state — strings everywhere so MUI text fields stay controlled even
// for transient invalid values (e.g. mid-edit "12."). The dirty check
// happens at save time after parsing back to the wire-shape types.
// ---------------------------------------------------------------------------

type FormState = {
  name: string;
  brand: string;
  upc: string;
  categoryId: string;
  description: string;
  /** Dollar-and-cents string ("12.34"); converted to integer cents on save. */
  priceText: string;
  costText: string;
  /** Raw text of the weight input, in `weightUnit`. Empty = no value
   *  entered. Canonical storage is integer grams; the entered text is
   *  ceil-converted on save. */
  weightText: string;
  weightUnit: WeightUnit;
  /** Raw text of each dimension axis, in `dimensionUnit`. Same
   *  ceil-on-save semantics as weight. */
  widthText: string;
  lengthText: string;
  heightText: string;
  dimensionUnit: DimensionUnit;
  hidden: boolean;
  discontinued: boolean;
  special: boolean;
  /** Sells at 0% HST. Also drives what the Square sync attaches to the
   *  catalog item — an exempt item carries no tax there. */
  taxExempt: boolean;
  /** "Featured product" — surfaces on the storefront Home page. Server
   *  carries a timestamp under the hood (`featured_at`); the dialog
   *  reads it back as a boolean (`true` when non-null). */
  featured: boolean;
  /** Soft-deleted. Same shape story as `featured`: backed by
   *  `deleted_at` on the server; the dialog renders it as a boolean
   *  checkbox. `true` hides the item from the customer-facing
   *  storefront and renders the admin card with a tombstone treatment;
   *  unchecking restores it. */
  deleted: boolean;
  supplierUrl: string;
  /** Mongo field is `distributorUrl`; labelled "Manufacturer URL" in the UI. */
  distributorUrl: string;
  /** location key -> integer stock count (as string so the input stays controlled). */
  inventory: Record<string, string>;
  /** Optional new product-image URL. When non-empty, Save uploads it to
   *  Sirv before patching other fields, then clears this field. */
  newImageUrl: string;
  /** Optional directly-selected/pasted image file. Takes precedence over
   *  `newImageUrl` on Save — it's the fallback for supplier pages the
   *  server can't fetch itself (Cloudflare, login walls). Cleared after
   *  a successful upload. */
  newImageFile: File | null;
  /** Per-personality body copy (personality slug -> textarea contents).
   *  These are edit-only for now — none can be marked as the
   *  customer-facing primary. The standalone `description` field above
   *  is always the primary. Letting a personality take over is a
   *  client-side concern we'll revisit later. */
  descriptions: Record<string, string>;
};

// Weight + dimension unit choices for the form. Canonical storage is
// integer grams and integer centimetres; the UI lets the operator enter
// in their preferred unit and ceil-converts on save (round-up so
// shipping packaging never under-spec's the parcel).
type WeightUnit = 'kg' | 'g' | 'lb';
type DimensionUnit = 'cm' | 'in';

const G_PER_KG = 1000;
const G_PER_LB = 453.59237; // exact, international avoirdupois pound
const CM_PER_IN = 2.54;

/** Convert raw form text in `unit` to integer grams. Returns null when
 *  the text is empty or unparseable — caller decides whether to skip
 *  the field or surface a validation error. Always rounds up. */
const textToGrams = (text: string, unit: WeightUnit): number | null => {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  if (!isFinite(n) || n < 0) return null;
  const grams =
    unit === 'kg' ? n * G_PER_KG : unit === 'lb' ? n * G_PER_LB : n;
  return Math.ceil(grams);
};

/** Convert raw dimension text in `unit` to integer cm. Same semantics
 *  as `textToGrams`. */
const textToCm = (text: string, unit: DimensionUnit): number | null => {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  if (!isFinite(n) || n < 0) return null;
  const cm = unit === 'in' ? n * CM_PER_IN : n;
  return Math.ceil(cm);
};

/** Inverse of `textToGrams` for populating the form on edit. Picks a
 *  display precision that's readable in the chosen unit without
 *  showing the underlying gram-integer noise. */
const gramsToText = (grams: number | null | undefined, unit: WeightUnit): string => {
  if (grams == null) return '';
  if (unit === 'g') return String(grams);
  if (unit === 'kg') return (grams / G_PER_KG).toFixed(3).replace(/\.?0+$/, '');
  // lb
  return (grams / G_PER_LB).toFixed(3).replace(/\.?0+$/, '');
};

/** Inverse of `textToCm`. */
const cmToText = (cm: number | null | undefined, unit: DimensionUnit): string => {
  if (cm == null) return '';
  if (unit === 'cm') return String(cm);
  return (cm / CM_PER_IN).toFixed(2).replace(/\.?0+$/, '');
};

const centsToText = (cents?: number | null): string => {
  if (cents == null) return '';
  return (cents / 100).toFixed(2);
};

/** Parse "12.34" → 1234 cents. Returns null on empty/invalid input so
 *  the caller can decide whether to send (skip) or surface an error. */
const textToCents = (text: string): number | null => {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  if (!isFinite(n)) return null;
  return Math.round(n * 100);
};

// Display SKU is computed, never edited. Server-side: tqos-server clears
// the stored value on read and recomputes the same way. Keeping the
// formula in two places is fine for now — it's a one-liner and the
// prefix table moves with the tenant config.
const displaySku = (itemId: string, skuPrefix: string): string => {
  const tail = itemId.slice(0, 6).toUpperCase();
  return `${skuPrefix}${tail}`;
};

/** Blank form for the "+ Add item" flow. categoryId is seeded from the
 *  category the admin opened the dialog from; inventory starts at zero
 *  for the single active location. */
const initialFromCreate = (defaultCategoryId: string): FormState => ({
  name: '',
  brand: '',
  upc: '',
  categoryId: defaultCategoryId,
  description: '',
  priceText: '',
  costText: '',
  weightText: '',
  weightUnit: 'kg',
  widthText: '',
  lengthText: '',
  heightText: '',
  dimensionUnit: 'cm',
  hidden: false,
  discontinued: false,
  special: false,
  taxExempt: false,
  featured: false,
  deleted: false,
  supplierUrl: '',
  distributorUrl: '',
  inventory: { bridgewater: '0' },
  newImageUrl: '',
  newImageFile: null,
  descriptions: {},
});

const initialFromItem = (item: AdminItem): FormState => ({
  name: item.name ?? '',
  brand: item.brand ?? '',
  upc: item.codes?.upc ?? '',
  categoryId: item.category_id ?? '',
  description: item.description ?? '',
  priceText: centsToText(item.price),
  costText: centsToText(item.cost),
  // Default to canonical units on load. Operator can flip the unit
  // dropdown and the textbox values re-render via the field setter
  // (see SectionDimensionsAndWeight below).
  weightText: gramsToText(item.weight_g, 'kg'),
  weightUnit: 'kg',
  widthText: cmToText(item.dimensions_cm?.width, 'cm'),
  lengthText: cmToText(item.dimensions_cm?.length, 'cm'),
  heightText: cmToText(item.dimensions_cm?.height, 'cm'),
  dimensionUnit: 'cm',
  hidden: !!item.flags?.hidden,
  discontinued: !!item.flags?.discontinued,
  special: !!item.flags?.special,
  taxExempt: !!item.flags?.taxExempt,
  featured: !!item.featured_at,
  deleted: !!item.deleted_at,
  // Only the first slot of supplierUrls is exposed; mirrors ags-admin.
  supplierUrl: item.supplier_urls?.[0] ?? '',
  distributorUrl: item.distributor_url ?? '',
  // Single active location: bridgewater. Legacy locations
  // (liverpool, warehouse, bongbus, overflow) on existing items are left
  // untouched on save because buildPatch only emits diffs for whatever
  // appears in `perLocation`, which we also restrict to bridgewater.
  inventory: {
    bridgewater: String(item.inventory?.bridgewater?.count ?? 0),
  },
  newImageUrl: '',
  newImageFile: null,
  descriptions: { ...(item.descriptions ?? {}) },
});

// Diff form state against the original item to build a minimal patch.
// Empty-string vs null is treated as "no change" when the source was
// also null/empty, so we don't send no-op writes.
const buildPatch = (
  original: AdminItem,
  form: FormState,
  perLocation: Record<string, number>,
): ItemPatch => {
  const patch: ItemPatch = {};

  const changedString = (formVal: string, origVal?: string | null) => {
    const o = (origVal ?? '').toString();
    return formVal !== o;
  };

  if (changedString(form.name, original.name)) patch.name = form.name;
  if (changedString(form.brand, original.brand)) patch.brand = form.brand;
  if (changedString(form.upc, original.codes?.upc)) patch.upc = form.upc;
  if (changedString(form.categoryId, original.category_id))
    patch.categoryId = form.categoryId;

  const newPrice = textToCents(form.priceText);
  if (newPrice !== null && newPrice !== (original.price ?? null))
    patch.price = newPrice;
  const newCost = textToCents(form.costText);
  if (newCost !== null && newCost !== (original.cost ?? null))
    patch.cost = newCost;

  // Weight + dimensions: convert to canonical integer units (ceil),
  // compare against the stored values. Both compare cleanly with `===`
  // because everything is integers — no epsilon needed.
  const newWeight = textToGrams(form.weightText, form.weightUnit);
  if (newWeight !== null && newWeight !== (original.weight_g ?? null))
    patch.weightG = newWeight;

  const origDims = original.dimensions_cm ?? {};
  const newWidth = textToCm(form.widthText, form.dimensionUnit);
  const newLength = textToCm(form.lengthText, form.dimensionUnit);
  const newHeight = textToCm(form.heightText, form.dimensionUnit);
  const widthChanged =
    newWidth !== null && newWidth !== (origDims.width ?? null);
  const lengthChanged =
    newLength !== null && newLength !== (origDims.length ?? null);
  const heightChanged =
    newHeight !== null && newHeight !== (origDims.height ?? null);
  if (widthChanged || lengthChanged || heightChanged) {
    // Server `$set`s the whole nested object — send every axis the
    // form has a value for, even unchanged ones, so we don't drop a
    // stored axis while changing another.
    const dims: { width?: number; length?: number; height?: number } = {};
    if (newWidth !== null) dims.width = newWidth;
    if (newLength !== null) dims.length = newLength;
    if (newHeight !== null) dims.height = newHeight;
    patch.dimensionsCm = dims;
  }

  if (form.hidden !== !!original.flags?.hidden) patch.hidden = form.hidden;
  if (form.discontinued !== !!original.flags?.discontinued)
    patch.discontinued = form.discontinued;
  if (form.special !== !!original.flags?.special) patch.special = form.special;
  if (form.taxExempt !== !!original.flags?.taxExempt)
    patch.taxExempt = form.taxExempt;
  if (form.featured !== !!original.featured_at) patch.featured = form.featured;
  if (form.deleted !== !!original.deleted_at) patch.deleted = form.deleted;

  if (changedString(form.supplierUrl, original.supplier_urls?.[0]))
    patch.supplierUrl = form.supplierUrl;
  if (changedString(form.distributorUrl, original.distributor_url))
    patch.distributorUrl = form.distributorUrl;

  // Standalone description (the customer-facing primary). Personalities
  // can't take over this slot right now — that's deliberately punted
  // until we decide how to model "select a different voice as primary"
  // without losing the standalone text on the round-trip.
  if (changedString(form.description, original.description))
    patch.description = form.description;

  // Per-personality descriptions: include slugs whose text changed
  // vs. the loaded item. Slugs the admin hasn't touched stay out of
  // the patch (no-op writes are pointless and would update the
  // top-level `updatedAt` for nothing).
  const origDescriptions = original.descriptions ?? {};
  const descDiff: Record<string, string> = {};
  for (const [slug, text] of Object.entries(form.descriptions)) {
    if (text !== (origDescriptions[slug] ?? '')) {
      descDiff[slug] = text;
    }
  }
  if (Object.keys(descDiff).length > 0) patch.descriptions = descDiff;

  // Per-location inventory diffs — only include locations whose count
  // actually differs from the freshly-fetched value.
  const invDiff: Record<string, number> = {};
  for (const [loc, count] of Object.entries(perLocation)) {
    const text = form.inventory[loc];
    if (text == null || text === '') continue;
    const n = parseInt(text, 10);
    if (!isFinite(n)) continue;
    if (n !== count) invDiff[loc] = n;
  }
  if (Object.keys(invDiff).length > 0) patch.inventory = invDiff;

  return patch;
};

/** Build the wire shape for POST /api/v1/items/:tenant. Empty / unset
 *  fields are omitted so the server stores `None` rather than empty
 *  strings — matches the convention used elsewhere in this dialog. */
const buildCreate = (form: FormState): ItemCreate => {
  const body: ItemCreate = {
    name: form.name.trim(),
    categoryId: form.categoryId,
  };
  if (form.brand.trim() !== '') body.brand = form.brand.trim();
  if (form.description.trim() !== '') body.description = form.description;
  const price = textToCents(form.priceText);
  if (price !== null) body.price = price;
  const cost = textToCents(form.costText);
  if (cost !== null) body.cost = cost;
  const weight = textToGrams(form.weightText, form.weightUnit);
  if (weight !== null) body.weightG = weight;
  const width = textToCm(form.widthText, form.dimensionUnit);
  const length = textToCm(form.lengthText, form.dimensionUnit);
  const height = textToCm(form.heightText, form.dimensionUnit);
  if (width !== null || length !== null || height !== null) {
    const dims: { width?: number; length?: number; height?: number } = {};
    if (width !== null) dims.width = width;
    if (length !== null) dims.length = length;
    if (height !== null) dims.height = height;
    body.dimensionsCm = dims;
  }
  if (form.upc.trim() !== '') body.upc = form.upc.trim();
  if (form.supplierUrl.trim() !== '')
    body.supplierUrl = form.supplierUrl.trim();
  if (form.distributorUrl.trim() !== '')
    body.distributorUrl = form.distributorUrl.trim();
  if (form.hidden) body.hidden = true;
  if (form.discontinued) body.discontinued = true;
  if (form.special) body.special = true;
  if (form.taxExempt) body.taxExempt = true;
  const inv: Record<string, number> = {};
  for (const [loc, text] of Object.entries(form.inventory)) {
    const n = parseInt(text, 10);
    if (isFinite(n) && n !== 0) inv[loc] = n;
  }
  if (Object.keys(inv).length > 0) body.inventory = inv;
  return body;
};

// ---------------------------------------------------------------------------

type Props = {
  open: boolean;
  tenant: string;
  /** Tenant's Sirv path prefix, used to render the item image. */
  imageRoot: string;
  /** Single-letter SKU prefix used to compute the display SKU. */
  skuPrefix: string;
  /** Edit when set; create-mode when null. */
  itemId: string | null;
  categories: AdminCategory[];
  /** Category id pre-selected in create mode. Ignored when itemId is set.
   *  Required when itemId is null (the only way to land in create mode). */
  defaultCategoryId?: string;
  onClose: () => void;
  /** Called after a successful save so the page can refresh its grid. */
  onSaved: () => void;
};

const ItemEditDialog: React.FC<Props> = ({
  open,
  tenant,
  imageRoot,
  skuPrefix,
  itemId,
  categories,
  defaultCategoryId,
  onClose,
  onSaved,
}) => {
  // Mode is implicit: null itemId = "create"; otherwise "edit". The
  // mode-conditional code paths (skip GET, swap title, call POST instead
  // of PATCH) all branch on this single derived value.
  const isCreate = itemId === null;
  const { idToken } = useAuth();
  const [item, setItem] = useState<AdminItem | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [perLocation, setPerLocation] = useState<Record<string, number>>({});
  // Personalities live in atlanticgrowshop's Mongo Config; backend caches
  // for 5 min so this fetch is cheap. Hold at the dialog level so each
  // open re-confirms (admin might have added a personality between
  // opens). The list rarely changes — keep it simple, no separate
  // module-level cache.
  const [personalities, setPersonalities] = useState<Personality[] | null>(null);
  const [loading, setLoading] = useState(false);
  // Two-step Save: image upload first (slow — Sirv round-trip), then
  // PATCH for other fields. The label switches so the admin sees
  // progress on the long step.
  const [saving, setSaving] = useState<'idle' | 'uploading' | 'saving'>('idle');
  const [error, setError] = useState<string>('');
  // Scrape: independent state from save/upload — admin can scrape while
  // they're still editing other fields. Tone says whether the message
  // is informational ("loaded") or a problem ("nothing found"/"failed").
  const [scraping, setScraping] = useState(false);
  const [scrapeMessage, setScrapeMessage] = useState<{
    tone: 'success' | 'warning' | 'error';
    text: string;
  } | null>(null);
  // Generate (Gemini): per-personality "Generate" buttons. We track
  // which slug is currently generating so only that row's spinner
  // shows; admins can fire one while editing other fields. Message is
  // per-slug too so a failure on one personality doesn't blank a
  // success on another.
  const [generatingSlug, setGeneratingSlug] = useState<string | null>(null);
  const [generateMessages, setGenerateMessages] = useState<
    Record<
      string,
      { tone: 'success' | 'warning' | 'error'; text: string }
    >
  >({});

  // Build the category dropdown: leaves only, labeled with the full
  // breadcrumb ("top / sub / leaf"). Tenant scoping is already handled
  // upstream — `categories` comes from `listCategories(idToken, tenant)`.
  //
  // A "leaf" is any category that no other category points to as its
  // parent. We hide non-leaves because filing an item against a parent
  // category breaks the items-browser layout (a node can hold either
  // child categories OR items, never both). One exception: when editing
  // an item whose existing categoryId is a non-leaf (legacy data), we
  // include that single option so the user can still save without
  // re-categorising — they can opt into a leaf if they want.
  //
  // `1` is the sentinel root parent id, mirrored from ItemsPage.
  const sortedCategories = useMemo(() => {
    const ROOT_PARENT_ID = '1';
    const byId = new Map(categories.map((c) => [c.id, c]));
    const parentIds = new Set<string>();
    for (const c of categories) {
      const p = c.parent_id ?? ROOT_PARENT_ID;
      if (p !== ROOT_PARENT_ID) parentIds.add(p);
    }
    const labelFor = (c: AdminCategory): string => {
      const trail: string[] = [];
      let cur: AdminCategory | undefined = c;
      // Bound the walk: a malformed parent chain could loop forever.
      // The shop has < 32 levels in practice; bail past that.
      let safety = 0;
      while (cur && safety < 32) {
        trail.unshift(cur.name);
        const pid = cur.parent_id ?? ROOT_PARENT_ID;
        if (pid === ROOT_PARENT_ID) break;
        cur = byId.get(pid);
        safety++;
      }
      return trail.join(' / ');
    };
    const editedNonLeaf = item?.category_id ?? null;
    const selectable = categories.filter(
      (c) => !parentIds.has(c.id) || c.id === editedNonLeaf,
    );
    return selectable
      .map((c) => ({ cat: c, label: labelFor(c) }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [categories, item?.category_id]);

  // Personalities list — fetch in parallel with the item. Empty list on
  // failure (we render the dialog without a Descriptions section rather
  // than blocking the whole edit on personalities being unavailable).
  useEffect(() => {
    if (!open || !idToken) return;
    let cancelled = false;
    listPersonalities(idToken)
      .then((rows) => {
        if (!cancelled) setPersonalities(rows);
      })
      .catch(() => {
        if (!cancelled) setPersonalities([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, idToken]);

  // Wipe the per-personality "Generated." toast, the supplier scrape
  // toast, and the in-flight slug whenever the dialog opens for a
  // new item (or closes). These slots are keyed by personality slug
  // and supplier URL, not by item id — without an explicit reset, a
  // success message left over from editing item A re-appears next to
  // the matching personality on item B, even though item B's form
  // data is correct. Looks like data pollution, isn't.
  const resetTransientFeedback = () => {
    setGenerateMessages({});
    setGeneratingSlug(null);
    setScrapeMessage(null);
    setScraping(false);
  };

  // Create mode: seed a blank form the moment the dialog opens. No GET
  // to make; the new item doesn't exist yet.
  useEffect(() => {
    if (!open || !isCreate) return;
    setError('');
    setItem(null);
    setForm(initialFromCreate(defaultCategoryId ?? ''));
    setPerLocation({});
    resetTransientFeedback();
  }, [open, isCreate, defaultCategoryId]);

  // Fetch fresh item when the dialog opens with a non-null id. We don't
  // reuse the list snapshot — another admin might have edited it since
  // the page loaded, and we'd silently overwrite their changes.
  useEffect(() => {
    if (!open || !itemId || !idToken) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    setItem(null);
    setForm(null);
    resetTransientFeedback();
    getItem(idToken, tenant, itemId)
      .then((fresh) => {
        if (cancelled) return;
        setItem(fresh);
        setForm(initialFromItem(fresh));
        // Only bridgewater is editable; the diff in buildPatch only
        // looks at keys present here, so legacy locations never get
        // overwritten by accident.
        setPerLocation({
          bridgewater: fresh.inventory?.bridgewater?.count ?? 0,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setError('Item not found.');
        } else {
          setError(err?.message || 'Failed to load item');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, itemId, idToken, tenant]);

  const handleClose = () => {
    if (saving !== 'idle') return; // don't drop the dialog mid-save
    setItem(null);
    setForm(null);
    setError('');
    resetTransientFeedback();
    onClose();
  };

  const handleSave = async () => {
    if (!form || !idToken) return;
    setError('');

    // ---- Create mode -------------------------------------------------
    // POST first to get the new id, then (if the admin pasted an image
    // URL) upload to /:id/image. Doing it in this order avoids needing
    // a separate "upload first, retain id" round-trip — Sirv only sees
    // the upload if the create succeeded.
    if (isCreate) {
      if (form.name.trim() === '') {
        setError('Name is required.');
        return;
      }
      if (form.categoryId === '') {
        setError('Category is required.');
        return;
      }
      setSaving('saving');
      try {
        const created = await createItem(idToken, tenant, buildCreate(form));
        const newUrl = form.newImageUrl.trim();
        if (form.newImageFile || newUrl !== '') {
          setSaving('uploading');
          try {
            if (form.newImageFile) {
              await uploadItemImage(idToken, tenant, created.id, form.newImageFile);
            } else {
              await setItemImage(idToken, tenant, created.id, newUrl);
            }
          } catch (err: any) {
            // The item is already created — surface the upload failure
            // but still treat the save as successful enough to close.
            // Admin can re-open the new item from the grid and try the
            // image again. Silent failure here would be worse: they'd
            // think nothing was saved and try the whole form again.
            setSaving('idle');
            setError(
              err instanceof ApiError && err.status === 503
                ? 'Item created, but image upload is not configured on the server.'
                : `Item created, but image upload failed: ${err?.message || 'unknown error'}`,
            );
            onSaved();
            return;
          }
        }
        onSaved();
        setSaving('idle');
        setItem(null);
        setForm(null);
        onClose();
      } catch (err: any) {
        setSaving('idle');
        setError(err?.message || 'Create failed');
      }
      return;
    }

    // ---- Edit mode ---------------------------------------------------
    if (!item) return;

    // Step 1: image upload, if a new URL was entered. Done first because
    // it's the slow + lossy step — if it fails, no field writes happen
    // and the dialog stays open with the original values intact.
    let workingItem = item;
    const newUrl = form.newImageUrl.trim();
    if (form.newImageFile || newUrl !== '') {
      setSaving('uploading');
      try {
        workingItem = form.newImageFile
          ? await uploadItemImage(idToken, tenant, item.id, form.newImageFile)
          : await setItemImage(idToken, tenant, item.id, newUrl);
        setItem(workingItem);
        setField('newImageUrl', '');
        setField('newImageFile', null);
      } catch (err: any) {
        setSaving('idle');
        setError(
          err instanceof ApiError && err.status === 503
            ? 'Image upload is not configured on the server.'
            : err?.message || 'Image upload failed',
        );
        return;
      }
    }

    // Step 2: field patch. Diff against `workingItem` so an image we
    // just uploaded doesn't show up as a dirty change.
    setSaving('saving');
    try {
      const patch = buildPatch(workingItem, form, perLocation);
      if (Object.keys(patch).length > 0) {
        await patchItem(idToken, tenant, workingItem.id, patch);
      }
      onSaved();
      setSaving('idle');
      // Direct close — handleClose's saving guard would now block since
      // we already set saving back to idle, but reset state explicitly.
      setItem(null);
      setForm(null);
      onClose();
    } catch (err: any) {
      setSaving('idle');
      setError(err?.message || 'Save failed');
    }
  };

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => (f ? { ...f, [key]: value } : f));
  };

  // Object URL for previewing a staged (file/paste) image before upload.
  // Recomputed when the staged file changes; revoked on change/unmount so
  // we don't leak blob URLs.
  const stagedPreviewUrl = useMemo(
    () => (form?.newImageFile ? URL.createObjectURL(form.newImageFile) : null),
    [form?.newImageFile],
  );
  useEffect(() => {
    return () => {
      if (stagedPreviewUrl) URL.revokeObjectURL(stagedPreviewUrl);
    };
  }, [stagedPreviewUrl]);

  // Stage a file (from the file picker or a clipboard paste) for upload on
  // Save. Clears any typed URL so the two inputs don't compete, and drops
  // any prior error.
  const stageImageFile = (file: File) => {
    setField('newImageFile', file);
    setField('newImageUrl', '');
    setError('');
  };

  // Grab an image off the clipboard (Ctrl/Cmd+V) anywhere in the form.
  // Only acts when the clipboard actually carries an image file, so
  // pasting text into a field is unaffected.
  const handleImagePaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i += 1) {
      const it = items[i];
      if (it.kind === 'file' && it.type.startsWith('image/')) {
        const file = it.getAsFile();
        if (file) {
          e.preventDefault();
          stageImageFile(file);
          return;
        }
      }
    }
  };

  const handleGenerate = async (slug: string) => {
    if (!form || !item || !idToken) return;
    if (generatingSlug !== null) return; // one at a time
    setGeneratingSlug(slug);
    setGenerateMessages((m) => {
      const next = { ...m };
      delete next[slug];
      return next;
    });
    try {
      const result = await generateDescription(idToken, tenant, item.id, slug);
      if (result.text.trim() !== '') {
        setField('descriptions', {
          ...form.descriptions,
          [slug]: result.text,
        });
        setGenerateMessages((m) => ({
          ...m,
          [slug]: { tone: 'success', text: 'Generated.' },
        }));
      } else {
        // Gemini hit the UNKNOWN sentinel. Don't overwrite whatever
        // text was already there — the admin's draft is more valuable
        // than a confirmed "I don't know" from the model.
        setGenerateMessages((m) => ({
          ...m,
          [slug]: {
            tone: 'warning',
            text: 'Gemini could not identify this product.',
          },
        }));
      }
    } catch (e: any) {
      const status = e instanceof ApiError ? e.status : undefined;
      const friendly =
        status === 503
          ? 'AI generation is not configured on the server.'
          : status === 429
            ? 'Gemini rate-limited; try again in a moment.'
            : status === 502
              ? 'Gemini API call failed.'
              : e?.message || 'Generation failed.';
      setGenerateMessages((m) => ({
        ...m,
        [slug]: { tone: 'error', text: friendly },
      }));
    } finally {
      setGeneratingSlug(null);
    }
  };

  const handleScrape = async () => {
    if (!form || !idToken) return;
    const url = form.supplierUrl.trim();
    if (url === '') return;
    setScraping(true);
    setScrapeMessage(null);
    try {
      const result = await scrapeDescription(idToken, url);
      if (result.description && result.description.trim() !== '') {
        // Replace the standalone description with the scraped text. The
        // admin can edit before saving — we don't auto-commit.
        setField('description', result.description);
        setScrapeMessage({
          tone: 'success',
          text: 'Description loaded from supplier page.',
        });
      } else {
        setScrapeMessage({
          tone: 'warning',
          text: 'No description metadata found on that page.',
        });
      }
    } catch (e: any) {
      const status = e instanceof ApiError ? e.status : undefined;
      const friendly =
        status === 502
          ? 'Could not reach the supplier page.'
          : status === 422
            ? 'Supplier page response was not HTML.'
            : status === 400
              ? 'Supplier returned an error for that URL.'
              : e?.message || 'Scrape failed.';
      setScrapeMessage({ tone: 'error', text: friendly });
    } finally {
      setScraping(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        {isCreate
          ? 'New item'
          : item
            ? `Edit item — ${item.name}`
            : 'Edit item'}
        {!isCreate && item && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 0.25,
              color: '#94a3b8',
              fontFamily: 'monospace',
            }}
          >
            {item.id}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {form && (isCreate || item) && (
          <Stack spacing={2.5} onPaste={handleImagePaste}>
            <SectionTitle>Basic</SectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={12} sm="auto">
                {form.newImageFile && stagedPreviewUrl ? (
                  // Staged file/paste preview — shows before Save uploads
                  // it, and works in create mode (before the item exists).
                  <Box
                    sx={{
                      width: 160,
                      height: 160,
                      borderRadius: 1,
                      overflow: 'hidden',
                      border: '1px solid #cbd5e1',
                    }}
                  >
                    <img
                      src={stagedPreviewUrl}
                      alt="Staged upload preview"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                  </Box>
                ) : item ? (
                  <ItemImage
                    id={item.image_id}
                    imageRoot={imageRoot}
                    title={item.name}
                    size={160}
                  />
                ) : (
                  // No image until the item exists. Reserve the same
                  // 160px square so the rest of the form doesn't jump
                  // around when switching modes.
                  <Box
                    sx={{
                      width: 160,
                      height: 160,
                      border: '1px dashed #cbd5e1',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#94a3b8',
                      fontSize: '0.8rem',
                      textAlign: 'center',
                      px: 1,
                    }}
                  >
                    Image uploads after the item is created.
                  </Box>
                )}
              </Grid>
              <Grid item xs={12} sm container spacing={2}>
                <Grid item xs={12} sm={8}>
                  <TextField
                    label="Name"
                    fullWidth
                    value={form.name}
                    onChange={(e) => setField('name', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Brand"
                    fullWidth
                    value={form.brand}
                    onChange={(e) => setField('brand', e.target.value)}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12}>
                {form.newImageFile ? (
                  // A file/paste is staged — the URL field is moot; show
                  // the staged file with a way to clear it and fall back
                  // to the URL input.
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ flexWrap: 'wrap' }}
                  >
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {form.newImageFile.name || 'Pasted image'} staged —
                      Save to upload.
                    </Typography>
                    <Button
                      size="small"
                      color="inherit"
                      onClick={() => setField('newImageFile', null)}
                    >
                      Remove
                    </Button>
                  </Stack>
                ) : (
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                  >
                    <TextField
                      label="New image URL"
                      fullWidth
                      value={form.newImageUrl}
                      onChange={(e) => setField('newImageUrl', e.target.value)}
                      helperText="Paste a URL, or upload/paste a file if the source blocks our server (e.g. Cloudflare). Save uploads it to Sirv."
                    />
                    <Button
                      component="label"
                      variant="outlined"
                      sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                    >
                      Upload file
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) stageImageFile(f);
                          // Reset so re-selecting the same file re-fires.
                          e.target.value = '';
                        }}
                      />
                    </Button>
                  </Stack>
                )}
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="category-select">Category</InputLabel>
                  <Select
                    labelId="category-select"
                    label="Category"
                    value={form.categoryId}
                    onChange={(e) =>
                      setField('categoryId', e.target.value as string)
                    }
                  >
                    {sortedCategories.map(({ cat, label }) => (
                      <MenuItem key={cat.id} value={cat.id}>
                        {label}
                        {cat.hidden ? ' (hidden)' : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {/*
                The standalone "Description" textarea is gone — the
                customer-facing primary copy is now whichever personality
                the admin selects in the Descriptions section below.
                Orphan-primary text (text not matching any personality)
                is surfaced as an Alert in that section so it isn't lost.
              */}
            </Grid>

            <SectionTitle>Pricing &amp; codes</SectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <TextField
                  label="Price"
                  fullWidth
                  value={form.priceText}
                  onChange={(e) => setField('priceText', e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                  inputProps={{ inputMode: 'decimal' }}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  label="Cost"
                  fullWidth
                  value={form.costText}
                  onChange={(e) => setField('costText', e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                  inputProps={{ inputMode: 'decimal' }}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  label="UPC"
                  fullWidth
                  value={form.upc}
                  onChange={(e) => setField('upc', e.target.value)}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  label="SKU"
                  fullWidth
                  disabled
                  value={item ? displaySku(item.id, skuPrefix) : ''}
                  helperText={
                    item
                      ? 'Computed from tenant + item id.'
                      : 'Assigned after the item is created.'
                  }
                />
              </Grid>
            </Grid>

            <SectionTitle>Dimensions &amp; weight</SectionTitle>
            <Typography
              variant="caption"
              sx={{ display: 'block', mb: 1, color: '#94a3b8' }}
            >
              Stored as integer grams / cm. Saving rounds up. Flipping the
              unit dropdown changes how new entries are interpreted; it
              doesn't re-convert what's already typed.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={8} sm={4}>
                <TextField
                  label="Weight"
                  fullWidth
                  value={form.weightText}
                  onChange={(e) => setField('weightText', e.target.value)}
                  inputProps={{ inputMode: 'decimal' }}
                />
              </Grid>
              <Grid item xs={4} sm={3}>
                <FormControl fullWidth>
                  <InputLabel id="weight-unit-select">Unit</InputLabel>
                  <Select
                    labelId="weight-unit-select"
                    label="Unit"
                    value={form.weightUnit}
                    onChange={(e) =>
                      setField('weightUnit', e.target.value as WeightUnit)
                    }
                  >
                    <MenuItem value="kg">kg</MenuItem>
                    <MenuItem value="g">g</MenuItem>
                    <MenuItem value="lb">lb</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={4} sm={3}>
                <TextField
                  label="Width"
                  fullWidth
                  value={form.widthText}
                  onChange={(e) => setField('widthText', e.target.value)}
                  inputProps={{ inputMode: 'decimal' }}
                />
              </Grid>
              <Grid item xs={4} sm={3}>
                <TextField
                  label="Length"
                  fullWidth
                  value={form.lengthText}
                  onChange={(e) => setField('lengthText', e.target.value)}
                  inputProps={{ inputMode: 'decimal' }}
                />
              </Grid>
              <Grid item xs={4} sm={3}>
                <TextField
                  label="Height"
                  fullWidth
                  value={form.heightText}
                  onChange={(e) => setField('heightText', e.target.value)}
                  inputProps={{ inputMode: 'decimal' }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel id="dimension-unit-select">Unit</InputLabel>
                  <Select
                    labelId="dimension-unit-select"
                    label="Unit"
                    value={form.dimensionUnit}
                    onChange={(e) =>
                      setField(
                        'dimensionUnit',
                        e.target.value as DimensionUnit,
                      )
                    }
                  >
                    <MenuItem value="cm">cm</MenuItem>
                    <MenuItem value="in">in</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <SectionTitle>Flags</SectionTitle>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.hidden}
                    onChange={(e) => setField('hidden', e.target.checked)}
                  />
                }
                label="Hidden"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.discontinued}
                    onChange={(e) =>
                      setField('discontinued', e.target.checked)
                    }
                  />
                }
                label="Discontinued"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.special}
                    onChange={(e) => setField('special', e.target.checked)}
                  />
                }
                label="Special"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.taxExempt}
                    onChange={(e) => setField('taxExempt', e.target.checked)}
                  />
                }
                label="Zero Rated"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.featured}
                    onChange={(e) => setField('featured', e.target.checked)}
                  />
                }
                label="Featured"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.deleted}
                    onChange={(e) => setField('deleted', e.target.checked)}
                    sx={{
                      // Red tint matches the tombstone palette used in
                      // the grid; signals "destructive flag" without
                      // an extra surrounding alert.
                      color: '#b91c1c',
                      '&.Mui-checked': { color: '#b91c1c' },
                    }}
                  />
                }
                label="Deleted"
              />
            </Stack>

            <SectionTitle>Source URLs</SectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Supplier URL"
                  fullWidth
                  value={form.supplierUrl}
                  onChange={(e) => setField('supplierUrl', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Manufacturer URL"
                  fullWidth
                  value={form.distributorUrl}
                  onChange={(e) =>
                    setField('distributorUrl', e.target.value)
                  }
                />
              </Grid>
            </Grid>

            {(() => {
              // In edit mode the location set comes from `perLocation`
              // (the freshly-fetched item's current counts, used as the
              // diff baseline). In create mode there's no baseline, so
              // we fall back to whatever keys the seeded form already
              // has — currently just bridgewater.
              const locs = isCreate
                ? Object.keys(form.inventory)
                : Object.keys(perLocation);
              if (locs.length === 0) return null;
              return (
                <>
                  <SectionTitle>Inventory</SectionTitle>
                  <Grid container spacing={2}>
                    {locs.map((loc) => (
                      <Grid key={loc} item xs={6} sm={3}>
                        <TextField
                          label={loc}
                          fullWidth
                          type="number"
                          value={
                            form.inventory[loc] ??
                            String(perLocation[loc] ?? 0)
                          }
                          onChange={(e) =>
                            setField('inventory', {
                              ...form.inventory,
                              [loc]: e.target.value,
                            })
                          }
                          inputProps={{ inputMode: 'numeric' }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </>
              );
            })()}

            <DescriptionsSection
              form={form}
              isCreate={isCreate}
              personalities={personalities}
              supplierUrl={form.supplierUrl}
              scraping={scraping}
              scrapeMessage={scrapeMessage}
              onScrape={handleScrape}
              onDismissScrapeMessage={() => setScrapeMessage(null)}
              generatingSlug={generatingSlug}
              generateMessages={generateMessages}
              onGenerate={handleGenerate}
              onDismissGenerateMessage={(slug) =>
                setGenerateMessages((m) => {
                  const next = { ...m };
                  delete next[slug];
                  return next;
                })
              }
              onDescriptionChange={(text) => setField('description', text)}
              onPersonalityTextChange={(slug, text) =>
                setField('descriptions', { ...form.descriptions, [slug]: text })
              }
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving !== 'idle'}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!form || loading || saving !== 'idle'}
        >
          {saving === 'uploading'
            ? 'Uploading image…'
            : saving === 'saving'
              ? isCreate
                ? 'Creating…'
                : 'Saving…'
              : isCreate
                ? 'Create'
                : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="overline"
    sx={{ color: '#64748b', letterSpacing: '0.1em', fontWeight: 600 }}
  >
    {children}
  </Typography>
);

const DescriptionsSection: React.FC<{
  form: FormState;
  /** Create-mode flag — when true the per-personality blocks are
   *  suppressed entirely (Generate endpoints require an item id that
   *  doesn't exist yet). The standalone Description still renders. */
  isCreate: boolean;
  /** null = still loading; [] = unreachable/empty (still render the
   *  standalone Description row though, which doesn't need them). */
  personalities: Personality[] | null;
  supplierUrl: string;
  scraping: boolean;
  scrapeMessage:
    | { tone: 'success' | 'warning' | 'error'; text: string }
    | null;
  onScrape: () => void;
  onDismissScrapeMessage: () => void;
  /** Slug of the personality currently being generated (or null). */
  generatingSlug: string | null;
  generateMessages: Record<
    string,
    { tone: 'success' | 'warning' | 'error'; text: string }
  >;
  onGenerate: (slug: string) => void;
  onDismissGenerateMessage: (slug: string) => void;
  onDescriptionChange: (text: string) => void;
  onPersonalityTextChange: (slug: string, text: string) => void;
}> = ({
  form,
  isCreate,
  personalities,
  supplierUrl,
  scraping,
  scrapeMessage,
  onScrape,
  onDismissScrapeMessage,
  generatingSlug,
  generateMessages,
  onGenerate,
  onDismissGenerateMessage,
  onDescriptionChange,
  onPersonalityTextChange,
}) => {
  // Empty-personality rows are collapsed by default to keep the dialog
  // navigable as the personality list grows (16+ voices already drafted
  // in tqos-server's personalities.md). Local state — resets on dialog
  // close — for which empty rows the admin has explicitly expanded this
  // session. Non-empty rows always render expanded.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const expand = (slug: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.add(slug);
      return next;
    });
  };

  return (
    <>
      <SectionTitle>Descriptions</SectionTitle>
      <Stack spacing={2}>
        {/* Standalone description — the customer-facing primary. Always
            rendered; doesn't wait on personalities to load. The Scrape
            button at the right pulls a description from the supplier
            URL above; disabled when no URL is set. */}
        <Box>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mb: 0.5 }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: '#1f2937' }}
            >
              Description
            </Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
              (customer-facing)
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Tooltip
              title={
                supplierUrl.trim() === ''
                  ? 'Set a Supplier URL above first.'
                  : 'Fetch description metadata from the supplier page.'
              }
            >
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={scraping || supplierUrl.trim() === ''}
                  onClick={onScrape}
                  startIcon={
                    scraping ? <CircularProgress size={14} /> : undefined
                  }
                >
                  {scraping ? 'Scraping…' : 'Scrape'}
                </Button>
              </span>
            </Tooltip>
          </Stack>
          {scrapeMessage && (
            <Alert
              severity={scrapeMessage.tone}
              onClose={onDismissScrapeMessage}
              sx={{ mb: 1 }}
            >
              {scrapeMessage.text}
            </Alert>
          )}
          <TextField
            fullWidth
            multiline
            minRows={3}
            value={form.description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            placeholder="Plain description shown to customers."
          />
        </Box>
        {isCreate && (
          // Per-personality copy needs an item id to generate against,
          // and most admins fill in voices after seeing the first save.
          // Tell them where to go rather than show empty disabled rows.
          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
            Per-personality descriptions can be added after the item is
            created — open it from the grid and use Generate.
          </Typography>
        )}
        {!isCreate && personalities === null && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
            <CircularProgress size={20} />
          </Box>
        )}
        {!isCreate && personalities?.map((p) => {
          const text = form.descriptions[p.id] ?? '';
          const isEmpty = text === '';
          const isGenerating = generatingSlug === p.id;
          // Show the textarea when there's content, when admin expanded
          // it manually, or while a generation is in flight (so the
          // result lands visibly rather than into a collapsed row).
          const showTextarea = !isEmpty || expanded.has(p.id) || isGenerating;
          const message = generateMessages[p.id];
          return (
            <Box key={p.id}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: showTextarea ? 0.5 : 0 }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    color: isEmpty ? '#64748b' : '#1f2937',
                  }}
                >
                  {p.displayName}
                </Typography>
                {p.disclaimer && (
                  <Tooltip title={p.disclaimer}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#94a3b8',
                        cursor: 'help',
                        fontStyle: 'italic',
                      }}
                    >
                      (about)
                    </Typography>
                  </Tooltip>
                )}
                <Box sx={{ flexGrow: 1 }} />
                {!showTextarea && (
                  <MuiLink
                    component="button"
                    type="button"
                    onClick={() => expand(p.id)}
                    sx={{ fontSize: '0.8rem', color: '#64748b' }}
                  >
                    add description
                  </MuiLink>
                )}
                <Tooltip
                  title={
                    generatingSlug !== null && !isGenerating
                      ? 'Wait for the current generation to finish.'
                      : `Generate a ${p.displayName} description with Gemini.`
                  }
                >
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={
                        generatingSlug !== null || form.name.trim() === ''
                      }
                      onClick={() => {
                        // Auto-expand so the generated text lands
                        // visibly; matches the Scrape pattern.
                        if (isEmpty) expand(p.id);
                        onGenerate(p.id);
                      }}
                      startIcon={
                        isGenerating ? (
                          <CircularProgress size={14} />
                        ) : undefined
                      }
                    >
                      {isGenerating ? 'Generating…' : 'Generate'}
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
              {message && (
                <Alert
                  severity={message.tone}
                  onClose={() => onDismissGenerateMessage(p.id)}
                  sx={{ mb: 1 }}
                >
                  {message.text}
                </Alert>
              )}
              {showTextarea && (
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  value={text}
                  onChange={(e) =>
                    onPersonalityTextChange(p.id, e.target.value)
                  }
                  placeholder={`No ${p.displayName} description yet.`}
                />
              )}
            </Box>
          );
        })}
      </Stack>
    </>
  );
};

export default ItemEditDialog;
